import PublicNav from "@/components/PublicNav";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Football, ArrowLeft, ArrowRight, Lightning, Scales, TrendUp, TrendDown, Users, ArrowsLeftRight, Trophy } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO, { BreadcrumbLd, FaqLd } from "@/components/SEO";
import { playerToSlug, compareUrl } from "@/lib/playerSlug";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>{pos}</span>;
}

function VotePill({ label, count, total, colorHex }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex-1 text-center py-2 border border-[#262626] bg-[#171717] rounded-sm relative overflow-hidden">
      <div className="absolute bottom-0 left-0 right-0 opacity-15" style={{ height: `${pct}%`, backgroundColor: colorHex }} />
      <p className="text-[10px] font-bold uppercase text-[#888] relative z-10">{label}</p>
      <p className="font-mono text-sm font-bold text-white relative z-10">{pct}%</p>
    </div>
  );
}

function TradeCard({ trade }) {
  const formatDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return ""; } };
  return (
    <div className="border border-[#262626] bg-[#171717] p-3">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[9px] text-[#555] uppercase tracking-wider">{formatDate(trade.date)}</p>
        {trade.involving && <p className="text-[9px] text-[#ccff00] uppercase tracking-wider">Trade involving {trade.involving}</p>}
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1">
          <p className="text-[8px] text-[#555] uppercase tracking-wider mb-0.5">SIDE A</p>
          {trade.side1.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position === "PICK" ? <span className="text-[9px] px-1.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
              <span className="text-xs text-white truncate">{p.name}</span>
            </div>
          ))}
        </div>
        <ArrowsLeftRight size={12} className="text-[#444] mt-3" />
        <div className="flex-1 space-y-1">
          <p className="text-[8px] text-[#555] uppercase tracking-wider mb-0.5">SIDE B</p>
          {trade.side2.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position === "PICK" ? <span className="text-[9px] px-1.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
              <span className="text-xs text-white truncate">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ComparisonPage() {
  const { pair } = useParams();
  const hideNav = useIsAppNavVisible();

  // Use preloaded data from build-time if available (prevents "Comparison not found" during bot/SSR render)
  const preloaded = typeof window !== "undefined" && window.__COMPARISON_DATA__?.player_a ? window.__COMPARISON_DATA__ : null;

  const [data, setData] = useState(preloaded || null);
  const [loading, setLoading] = useState(!preloaded);

  useEffect(() => {
    if (!pair) return;
    (async () => {
      if (!preloaded) setLoading(true);
      try {
        const res = await axios.get(`${API}/compare/${pair}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
        // Keep preloaded data if API fails
      }
      setLoading(false);
    })();
  }, [pair, preloaded]);

  // Generate canonical-ordered slug and display names for SEO
  const pairParts = pair?.split("-vs-") || [];
  const [sortedA, sortedB] = pairParts.length === 2 ? [pairParts[0], pairParts[1]].sort() : pairParts;
  const canonSlug = sortedA && sortedB ? `${sortedA}-vs-${sortedB}` : pair;
  const nameFromSlug = (s) => (s || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={`${nameFromSlug(pairParts[0])} vs ${nameFromSlug(pairParts[1])}: Dynasty Value Comparison | Who Should I Start/Draft?`} description={`Compare ${nameFromSlug(pairParts[0])} vs ${nameFromSlug(pairParts[1])} head-to-head dynasty trade values.`} path={`/compare/${canonSlug}`} />
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-4">
        <div className="h-8 w-64 skeleton rounded-sm" />
        <div className="h-48 skeleton rounded-sm" />
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={`${nameFromSlug(pairParts[0])} vs ${nameFromSlug(pairParts[1])}: Dynasty Comparison | Who Should I Start/Draft?`} description={`Dynasty trade value comparison.`} path={`/compare/${canonSlug}`} />
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-[#a3a3a3] text-lg mb-4">Comparison not found</p>
        <Link to="/rankings" className="text-[#ccff00] text-sm no-underline hover:underline">Browse rankings</Link>
      </div>
    </div>
  );

  const { player_a: a, player_b: b, comparison: cmp, analysis, votes_a, votes_b, trades, related } = data;
  const leader = cmp.leader === "a" ? a : cmp.leader === "b" ? b : null;
  const imgA = a.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${a.sleeperId}.jpg` : null;
  const imgB = b.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${b.sleeperId}.jpg` : null;
  const totalA = (votes_a?.buy || 0) + (votes_a?.hold || 0) + (votes_a?.sell || 0);
  const totalB = (votes_b?.buy || 0) + (votes_b?.hold || 0) + (votes_b?.sell || 0);
  const trailer = leader ? (leader === a ? b : a) : null;
  const compFaqs = analysis?.faq?.length > 0 ? analysis.faq : [
    { q: `Who has more dynasty trade value, ${a.name} or ${b.name}?`, a: leader ? `${leader.name} has more dynasty value right now — ${leader.value_sf?.toLocaleString()} vs ${trailer.value_sf?.toLocaleString()} in Superflex, a ${cmp.gap_pct}% edge based on FantasyCalc consensus from 150,000+ real leagues.` : `Their values are nearly even (${a.value_sf?.toLocaleString()} vs ${b.value_sf?.toLocaleString()}, within 5% of each other), so the right side depends on your roster build and contention timeline.` },
    { q: `Should I trade ${a.name} for ${b.name} straight up?`, a: leader ? `On raw market value the side getting ${leader.name} wins a straight-up swap by about ${cmp.gap_pct}%. To make it fair, the other side should add a pick or player — test exact packages in the free trade calculator.` : `A straight-up swap is close to fair on market value. Pick the player who better fits your timeline, or test add-ons in the free trade calculator.` },
    { q: `Who is the better long-term dynasty asset, ${a.name} or ${b.name}?`, a: `${a.name} is a ${a.position}${a.age ? ` at age ${a.age}` : ""} and ${b.name} is a ${b.position}${b.age ? ` at age ${b.age}` : ""}. Long-term, favor the younger player at the more valuable position for your build — the age and value trends above show how each is aging against the market.` },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title={`${a.name} vs ${b.name}: Dynasty Trade Value Comparison (2026) | Who Should I Start/Draft?`}
        description={`${a.name} vs ${b.name} dynasty comparison. ${leader ? `${leader.name} holds a ${cmp.gap_pct}% edge in Superflex value.` : "Values are nearly even."} Full breakdown with trade verdict.`}
        path={`/compare/${canonSlug}`}
      />
      <BreadcrumbLd items={[{ name: "Home", url: "/" }, { name: "Player Comparisons", url: "/compare" }, { name: `${a.name} vs ${b.name}`, url: `/compare/${canonSlug}` }]} />
      <FaqLd faqs={compFaqs} />
      <PublicNav />

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <Link to="/rankings" className="inline-flex items-center gap-1 text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline mb-4 uppercase tracking-wider"><ArrowLeft size={10} /> Rankings</Link>

        {/* H1 + Verdict */}
        <div className="mb-6" data-testid="comparison-hero">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white uppercase mb-3">
            {a.name} <span className="text-[#666]">vs</span> {b.name}
          </h1>
          <p className="text-sm text-[#a3a3a3]">
            {leader
              ? <><strong className="text-[#ccff00]">{leader.name}</strong> holds a <strong className="text-white">{cmp.gap_pct}%</strong> edge in Superflex dynasty value.</>
              : <>Values are <strong className="text-white">nearly even</strong> — within 5% of each other.</>}
            <span className="text-[#555] ml-2">Updated {new Date().toLocaleDateString()}</span>
          </p>
        </div>

        {/* Head-to-Head Table */}
        <div className="border border-[#262626] bg-[#171717] overflow-hidden mb-6" data-testid="h2h-table">
          <div className="grid grid-cols-3 bg-[#0a0a0a] border-b border-[#262626]">
            <div className="p-3 text-center">
              {imgA && <img src={imgA} alt={a.name} className="w-12 h-12 rounded-sm mx-auto mb-1 object-cover" onError={e => e.target.style.display='none'} />}
              <Link to={`/players/${a.slug}`} className="text-xs font-bold text-white no-underline hover:text-[#ccff00]">{a.name}</Link>
              <div className="mt-1"><PosBadge pos={a.position} /></div>
            </div>
            <div className="p-3 flex items-center justify-center"><span className="text-[9px] font-bold uppercase tracking-wider text-[#555]">STAT</span></div>
            <div className="p-3 text-center">
              {imgB && <img src={imgB} alt={b.name} className="w-12 h-12 rounded-sm mx-auto mb-1 object-cover" onError={e => e.target.style.display='none'} />}
              <Link to={`/players/${b.slug}`} className="text-xs font-bold text-white no-underline hover:text-[#ccff00]">{b.name}</Link>
              <div className="mt-1"><PosBadge pos={b.position} /></div>
            </div>
          </div>
          {[
            { label: "SF Value", va: a.value_sf?.toLocaleString(), vb: b.value_sf?.toLocaleString(), ha: a.value_sf > b.value_sf, hb: b.value_sf > a.value_sf },
            { label: "1QB Value", va: a.value_1qb?.toLocaleString() || "—", vb: b.value_1qb?.toLocaleString() || "—", ha: (a.value_1qb||0) > (b.value_1qb||0), hb: (b.value_1qb||0) > (a.value_1qb||0) },
            { label: "30d Trend", va: (a.trend_30d > 0 ? "+" : "") + a.trend_30d, vb: (b.trend_30d > 0 ? "+" : "") + b.trend_30d, ha: a.trend_30d > b.trend_30d, hb: b.trend_30d > a.trend_30d },
            { label: "Overall Rank", va: `#${a.rank_overall || "—"}`, vb: `#${b.rank_overall || "—"}`, ha: (a.rank_overall||999) < (b.rank_overall||999), hb: (b.rank_overall||999) < (a.rank_overall||999) },
            { label: "Pos Rank", va: `${a.position}#${a.rank_position || "—"}`, vb: `${b.position}#${b.rank_position || "—"}` },
            { label: "Age", va: a.age || "—", vb: b.age || "—", ha: (a.age||99) < (b.age||99), hb: (b.age||99) < (a.age||99) },
            { label: "Team", va: a.team || "FA", vb: b.team || "FA" },
            { label: "Tier", va: a.tier || "—", vb: b.tier || "—" },
          ].map(row => (
            <div key={row.label} className="grid grid-cols-3 border-b border-[#1a1a1a]">
              <div className={`p-2.5 text-center font-mono text-sm ${row.ha ? 'text-[#ccff00] font-bold' : 'text-white'}`}>{row.va}</div>
              <div className="p-2.5 text-center text-[9px] font-bold uppercase tracking-wider text-[#555]">{row.label}</div>
              <div className={`p-2.5 text-center font-mono text-sm ${row.hb ? 'text-[#ccff00] font-bold' : 'text-white'}`}>{row.vb}</div>
            </div>
          ))}
        </div>

        {/* Case for A / Case for B */}
        {analysis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="analysis-cases">
            <div className="border border-[#262626] bg-[#171717] p-4">
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-2">THE CASE FOR {a.name.split(" ").pop().toUpperCase()}</h2>
              <p className="text-sm text-[#a3a3a3] leading-relaxed">{analysis.case_for_a}</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-4">
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-2">THE CASE FOR {b.name.split(" ").pop().toUpperCase()}</h2>
              <p className="text-sm text-[#a3a3a3] leading-relaxed">{analysis.case_for_b}</p>
            </div>
          </div>
        )}

        {/* Situation Verdicts */}
        {analysis && (
          <div className="border border-[#262626] bg-[#171717] p-4 mb-6" data-testid="verdicts">
            <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">VERDICT BY TEAM SITUATION</h2>
            <div className="space-y-2">
              {[
                { label: "If you're contending:", text: analysis.verdict_contending },
                { label: "If you're rebuilding:", text: analysis.verdict_rebuilding },
                { label: "If value is close:", text: analysis.verdict_close },
              ].map(v => (
                <div key={v.label} className="flex items-start gap-2">
                  <span className="text-[10px] font-bold text-[#ccff00] uppercase shrink-0 mt-0.5 w-36">{v.label}</span>
                  <p className="text-xs text-[#a3a3a3]">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Community Pulse */}
        {(votes_a || votes_b) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="community-pulse">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mb-2">{a.name} — COMMUNITY PULSE</p>
              <div className="flex gap-1.5">
                <VotePill label="Buy" count={votes_a?.buy || 0} total={totalA} colorHex="#4ade80" />
                <VotePill label="Hold" count={votes_a?.hold || 0} total={totalA} colorHex="#fbbf24" />
                <VotePill label="Sell" count={votes_a?.sell || 0} total={totalA} colorHex="#ff3b30" />
              </div>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mb-2">{b.name} — COMMUNITY PULSE</p>
              <div className="flex gap-1.5">
                <VotePill label="Buy" count={votes_b?.buy || 0} total={totalB} colorHex="#4ade80" />
                <VotePill label="Hold" count={votes_b?.hold || 0} total={totalB} colorHex="#fbbf24" />
                <VotePill label="Sell" count={votes_b?.sell || 0} total={totalB} colorHex="#ff3b30" />
              </div>
            </div>
          </div>
        )}

        {/* Championship Rate */}
        {(a.championship || b.championship) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="championship-rates">
            {[a, b].map((player, i) => (
              <Link key={i} to="/championship-blueprint" className="border border-[#262626] bg-[#171717] p-4 no-underline hover:border-[#ccff00]/30 transition-colors flex items-center gap-3">
                <Trophy size={18} className="text-[#ccff00] shrink-0" weight="fill" />
                <div>
                  {player.championship ? (
                    <>
                      <p className="text-sm text-white font-bold">{player.championship.pct}% of champions</p>
                      <p className="text-[10px] text-[#888]">{player.name} was on {player.championship.count?.toLocaleString()} championship rosters</p>
                    </>
                  ) : (
                    <p className="text-xs text-[#555]">{player.name} — outside top 50 champion players</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Recent Trades */}
        {trades?.length > 0 && (
          <div className="mb-6" data-testid="recent-trades">
            <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3"><ArrowsLeftRight size={12} className="inline mr-1" /> RECENT REAL TRADES</h2>
            <div className="space-y-2">
              {trades.slice(0, 5).map((t, i) => <TradeCard key={i} trade={t} />)}
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="mb-6" data-testid="faq-section">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">FREQUENTLY ASKED</h2>
          <div className="space-y-2">
            {compFaqs.map((f, i) => (
              <div key={i} className="border border-[#262626] bg-[#171717] p-3">
                <p className="text-sm text-white font-semibold mb-1">{f.q}</p>
                <p className="text-xs text-[#888]">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trade CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 text-center mb-6" data-testid="trade-cta">
          <h3 className="font-heading text-lg text-white uppercase mb-2">RUN THIS TRADE YOURSELF</h3>
          <p className="text-xs text-[#888] mb-4">See exactly what {a.name} and {b.name} are worth side-by-side in our Trade Calculator.</p>
          <Link to="/trade-calculator" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold">
            <Lightning size={12} weight="fill" /> OPEN TRADE CALCULATOR
          </Link>
        </div>

        {/* Related Comparisons + Player Links */}
        {related?.length > 0 && (
          <div className="mb-6" data-testid="related-comparisons">
            <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">RELATED COMPARISONS</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {related.slice(0, 6).map((r, i) => (
                <div key={i} className="space-y-1">
                  <Link to={compareUrl(a.name, r.name)} className="block px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                    <span className="text-[10px] text-[#a3a3a3]">{a.name.split(" ").pop()} vs {r.name.split(" ").pop()}</span>
                  </Link>
                  <Link to={compareUrl(b.name, r.name)} className="block px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                    <span className="text-[10px] text-[#a3a3a3]">{b.name.split(" ").pop()} vs {r.name.split(" ").pop()}</span>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Player Profile Links */}
        <div className="flex gap-3 mb-6">
          <Link to={`/players/${a.slug}`} className="flex-1 text-center px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
            <span className="text-xs text-[#ccff00]">{a.name} Full Profile</span>
          </Link>
          <Link to={`/players/${b.slug}`} className="flex-1 text-center px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
            <span className="text-xs text-[#ccff00]">{b.name} Full Profile</span>
          </Link>
          <Link to="/rankings" className="flex-1 text-center px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
            <span className="text-xs text-[#a3a3a3]">Full Rankings</span>
          </Link>
        </div>

        {/* Research & Tools */}
        <div className="border-t border-[#262626] pt-8 mb-6">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-4">RESEARCH & TOOLS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <Link to="/research/what-championship-rosters-have-in-common" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">What Championship Rosters Have in Common</p>
              <p className="text-[10px] text-[#888]">78.4% had a top-10 RB. The real champion profile from 9,983 rosters.</p>
            </Link>
            <Link to="/research/stars-vs-depth" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">Stars vs Depth</p>
              <p className="text-[10px] text-[#888]">Champions held 38.3% of value in 3 players. Concentrate, don't spread.</p>
            </Link>
            <Link to="/research/who-wins-lopsided-trades" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">Who Wins Lopsided Trades?</p>
              <p className="text-[10px] text-[#888]">The value "loser" wins titles more often. Don't fear the overpay.</p>
            </Link>
            <Link to="/research/championship-dna-players" className="group border border-[#262626] bg-[#171717] p-3 rounded-sm no-underline hover:border-[#ccff00]/30 transition-colors">
              <p className="text-xs font-bold text-white mb-0.5 group-hover:text-[#ccff00] transition-colors">Championship DNA Players</p>
              <p className="text-[10px] text-[#888]">Bijan 23%, Puka 19.3%. The players who keep showing up on title teams.</p>
            </Link>
          </div>
          <div className="flex flex-wrap gap-3 text-[10px]">
            <Link to="/trade-calculator" className="text-[#ccff00] no-underline hover:underline">Trade Calculator</Link>
            <Link to="/trades" className="text-[#ccff00] no-underline hover:underline">Real Trade Database</Link>
            <Link to="/players" className="text-[#ccff00] no-underline hover:underline">All Players</Link>
            <Link to="/compare" className="text-[#ccff00] no-underline hover:underline">All Comparisons</Link>
            <Link to="/research" className="text-[#ccff00] no-underline hover:underline">33 Research Articles</Link>
            <Link to="/guide" className="text-[#ccff00] no-underline hover:underline">Dynasty Guides</Link>
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
        </div>
      </div>
    </div>
  );
}

function Nav() {
  return (
    <div className="border-b border-[#262626]">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center rounded-sm"><Football size={14} weight="bold" color="#000" /></div>
          <span className="font-heading text-xl text-white">DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/rankings" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Rankings</Link>
          <Link to="/trade-calculator" className="text-xs text-[#ccff00] hover:text-white no-underline font-bold hidden sm:block">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
